import { CheckCircle2, MailX } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { axiosInstance } from '@/axios';

import { ReactComponent as Logo } from '../../assets/logo-hs.svg';
import {
  UnsubscribeCard, UnsubscribeDescription, UnsubscribeIcon, UnsubscribeLogo, UnsubscribeTitle,
  UnsubscribeWrapper,
} from './Unsubscribe.styled';

type Status = 'working' | 'done' | 'error' | 'missing';

/**
 * Strona wypisania z wysyłek, otwierana z linku w stopce maila.
 *
 * STOI POZA LOGOWANIEM I POZA PREFIKSEM JĘZYKA: człowiek klika ją z poczty, często bez
 * sesji w panelu, a wymaganie zalogowania po to, żeby przestać dostawać wiadomości, jest
 * sprzeczne z sensem tego linku. Adres przychodzi w parametrze `email`, ten sam, który
 * backend wstawił do treści.
 */
export const Unsubscribe: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = (searchParams.get('email') || '').trim();
  const [status, setStatus] = useState<Status>(email ? 'working' : 'missing');

  useEffect(() => {
    if (!email) return;
    let cancelled = false;

    axiosInstance
      .post('/v3-unsubscribeEmail', { email })
      .then(() => { if (!cancelled) setStatus('done'); })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => { cancelled = true; };
  }, [email]);

  return (
    <UnsubscribeWrapper>
      <UnsubscribeCard>
        <UnsubscribeLogo><Logo /></UnsubscribeLogo>

        <UnsubscribeIcon $done={status === 'done'}>
          {status === 'done' ? <CheckCircle2 /> : <MailX />}
        </UnsubscribeIcon>

        {status === 'working' && (
          <>
            <UnsubscribeTitle>{t('unsubscribe.working', 'Wypisujemy adres')}</UnsubscribeTitle>
            <UnsubscribeDescription>{email}</UnsubscribeDescription>
          </>
        )}

        {status === 'done' && (
          <>
            <UnsubscribeTitle>{t('unsubscribe.doneTitle', 'Gotowe, nie wyślemy już wiadomości')}</UnsubscribeTitle>
            <UnsubscribeDescription>
              {t('unsubscribe.doneDescription', {
                defaultValue: 'Adres {{email}} nie będzie już dostawał wiadomości od hotelspot. Powiadomienia o zamówieniach i rezerwacjach z Twojego obiektu działają dalej.',
                email,
              })}
            </UnsubscribeDescription>
          </>
        )}

        {status === 'missing' && (
          <>
            <UnsubscribeTitle>{t('unsubscribe.missingTitle', 'Brak adresu w linku')}</UnsubscribeTitle>
            <UnsubscribeDescription>
              {t('unsubscribe.missingDescription', 'Otwórz link prosto z wiadomości, powinien nieść adres e-mail.')}
            </UnsubscribeDescription>
          </>
        )}

        {status === 'error' && (
          <>
            <UnsubscribeTitle>{t('unsubscribe.errorTitle', 'Nie udało się wypisać adresu')}</UnsubscribeTitle>
            <UnsubscribeDescription>
              {t('unsubscribe.errorDescription', 'Spróbuj jeszcze raz za chwilę albo odpisz na wiadomość, wypiszemy Cię ręcznie.')}
            </UnsubscribeDescription>
          </>
        )}
      </UnsubscribeCard>
    </UnsubscribeWrapper>
  );
};
